import type { WorksheetDocument } from '@abgen/model';

export interface ApiUser {
  id: string;
  email: string;
  displayName: string;
  /** `admin` betreut die Instanz und verwaltet Konten (Vorgabe V5). */
  role: 'admin' | 'teacher';
  mustChangePassword: boolean;
  active: boolean;
}

export interface AuthState {
  needsFirstAccount: boolean;
  localSetupRequired?: boolean;
  registrationOpen: boolean;
}

export interface DocumentSummary {
  id: string;
  title: string;
  output: string;
  tags: string[];
  updatedAt: string;
  revision: number;
  /** Vorschaubild der ersten Seite als SVG. */
  preview: string;
  /** In der Schulbibliothek sichtbar (Phase 8.4). */
  sharedWithSchool?: boolean;
  /** Wer das Blatt freigegeben hat. */
  ownerName?: string;
}

export interface RevisionSummary {
  revision: number;
  createdAt: string;
  title: string;
}

export interface TemplateSummary {
  id: string;
  title: string;
  category: string;
  scope: 'private' | 'school' | 'builtin';
  description?: string;
  preview: string;
  favorite: boolean;
  isDefault: boolean;
  builtIn: boolean;
  updatedAt?: string;
}

export interface PlayCode {
  code: string;
  collectResults: boolean;
  createdAt: string;
}

export interface SubmissionSummary {
  pseudonymId: string;
  documentRevision: number;
  answers: Record<string, string | number | boolean | null>;
  attempts: Record<string, number>;
  completed: boolean;
  completedAt: string | null;
  currentRevision?: number;
  correct?: number | null;
  total?: number | null;
  details?: {
    label: string;
    page: number;
    answer: string | number | boolean | null;
    correct: boolean;
    attempts: number;
  }[];
}

export interface UserDictionary {
  /** Wort → Silben mit „=“ (Phase 5.1). */
  syllables: Record<string, string>;
  /** Eigene Wörter für die Rechtschreibprüfung. */
  spelling: string[];
  /** Eigene Themenlisten für das Wortsuchrätsel. */
  wordsearchTopics: Record<string, string[]>;
}

export interface ShareSummary {
  tokenHash: string;
  createdAt: string;
  expiresAt: string | null;
}

/** Einstellungen des Druckdialogs (Phase 4.2). */
export interface PrintOptions {
  mode: 'worksheet' | 'solution' | 'both';
  variants: number;
  reroll: boolean;
  jobSeed?: number;
  bleed: boolean;
  /** Niveau-Variante (Phase 8.2); ohne Angabe alle Felder. */
  level?: 'basic' | 'standard' | 'advanced' | undefined;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly problems?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: { 'X-Abgen-Request': '1', ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
    ...init,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      problems?: { path: string; message: string }[];
    };
    throw new ApiError(
      response.status,
      body.message ?? body.error ?? `Der Server antwortete mit ${response.status}.`,
      body.problems,
    );
  }
  return (await response.json()) as T;
}

/** Dateien herunterladen: Antwort als Blob samt Dateiname aus dem Header. */
async function download(
  path: string,
  init: RequestInit = {},
): Promise<{ blob: Blob; fileName: string }> {
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: { 'X-Abgen-Request': '1', ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
    ...init,
  });
  if (!response.ok) {
    const problem = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    throw new ApiError(
      response.status,
      problem.message ?? problem.error ?? `Der Server antwortete mit ${response.status}.`,
    );
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await response.blob(), fileName: match?.[1] ?? 'download' };
}

/** Datei im Browser speichern. */
export function saveBlob({ blob, fileName }: { blob: Blob; fileName: string }): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  // Objekt-URLs geben ihren Speicher erst beim Widerrufen frei.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const api = {
  authState: () => request<AuthState>('/auth/state'),

  me: () => request<{ user: ApiUser }>('/auth/me'),

  login: (email: string, password: string) =>
    request<{ user: ApiUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  register: (email: string, password: string, displayName: string) =>
    request<{ user: ApiUser }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName }),
    }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  changePassword: (current: string, next: string) =>
    request<{ ok: true }>('/auth/password', {
      method: 'POST',
      body: JSON.stringify({ current, next }),
    }),

  listUsers: () => request<{ users: ApiUser[] }>('/users'),

  createUser: (email: string, displayName: string, role: 'admin' | 'teacher') =>
    request<{ user: ApiUser; password: string }>('/users', {
      method: 'POST',
      body: JSON.stringify({ email, displayName, role }),
    }),

  updateUser: (
    id: string,
    patch: Partial<Pick<ApiUser, 'displayName' | 'email' | 'role' | 'active'>>,
  ) =>
    request<{ user: ApiUser }>(`/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  resetPassword: (id: string) =>
    request<{ password: string; user: ApiUser }>(`/users/${id}/password`, { method: 'POST' }),

  listDocuments: (query?: string) =>
    request<{ documents: DocumentSummary[] }>(
      `/documents${query ? `?q=${encodeURIComponent(query)}` : ''}`,
    ),

  createDocument: (document: WorksheetDocument) =>
    request<{ id: string; revision: number; updatedAt: string }>('/documents', {
      method: 'POST',
      body: JSON.stringify({ document }),
    }),

  /** `token` ersetzt die Anmeldung beim Drucken und bei geteilten Blättern (Phase 4). */
  getDocument: (id: string, token?: string) =>
    request<{ id: string; revision: number; updatedAt: string; document: WorksheetDocument }>(
      `/documents/${id}${token ? `?token=${encodeURIComponent(token)}` : ''}`,
    ),

  saveDocument: (id: string, document: WorksheetDocument) =>
    request<{ id: string; revision: number; updatedAt: string }>(`/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ document }),
    }),

  // --- Phase 8.4: Schulbibliothek ---

  schoolDocuments: (query?: string) =>
    request<{ documents: DocumentSummary[] }>(
      `/documents/school${query ? `?q=${encodeURIComponent(query)}` : ''}`,
    ),

  setSchoolShare: (id: string, shared: boolean) =>
    request<{ shared: boolean }>(`/documents/${id}/school`, {
      method: 'PUT',
      body: JSON.stringify({ shared }),
    }),

  copyDocument: (id: string) =>
    request<{ id: string; title: string }>(`/documents/${id}/copy`, { method: 'POST' }),

  deleteDocument: (id: string) => request<{ ok: true }>(`/documents/${id}`, { method: 'DELETE' }),

  // --- Phase 4: Drucken, Vorlagen, Austausch ---

  /** PDF erzeugen lassen; `token` erlaubt den Druck über einen Freigabe-Link. */
  printPdf: (id: string, options: PrintOptions, token?: string) =>
    download(`/documents/${id}/pdf`, {
      method: 'POST',
      body: JSON.stringify(token ? { ...options, token } : options),
    }),

  exportDocument: (id: string) => download(`/documents/${id}/export`),

  importDocument: async (file: File) => {
    const body = new FormData();
    body.set('file', file);
    const response = await fetch('/api/documents/import', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Abgen-Request': '1' },
      body,
    });
    if (!response.ok) {
      const problem = (await response.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
      };
      throw new ApiError(
        response.status,
        problem.message ?? problem.error ?? 'Die Datei ließ sich nicht einlesen.',
      );
    }
    return (await response.json()) as { id: string; title: string };
  },

  templates: () =>
    request<{ templates: TemplateSummary[]; defaultTemplateId: string | null }>('/templates'),

  saveTemplate: (input: {
    title: string;
    category: string;
    scope: 'private' | 'school';
    document: WorksheetDocument;
  }) =>
    request<{ id: string; title: string }>('/templates', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  deleteTemplate: (id: string) => request<{ ok: true }>(`/templates/${id}`, { method: 'DELETE' }),

  setTemplateFavorite: (id: string, favorite: boolean) =>
    request<{ id: string; favorite: boolean }>(`/templates/${id}/favorite`, {
      method: 'PUT',
      body: JSON.stringify({ favorite }),
    }),

  setDefaultTemplate: (templateId: string | null) =>
    request<{ templateId: string | null }>('/templates/default', {
      method: 'PUT',
      body: JSON.stringify({ templateId }),
    }),

  createFromTemplate: (templateId: string, title?: string) =>
    request<{ id: string; revision: number; updatedAt: string }>('/documents/from-template', {
      method: 'POST',
      body: JSON.stringify(title ? { templateId, title } : { templateId }),
    }),

  /** Persönliches Wörterbuch: Silben, eigene Wörter, Wortsuch-Themen. */
  dictionary: () =>
    request<{ dictionary: UserDictionary }>('/dictionary').then((result) => result.dictionary),

  saveDictionary: (patch: Partial<UserDictionary>) =>
    request<{ dictionary: UserDictionary }>('/dictionary', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }).then((result) => result.dictionary),

  // --- Phase 7: digitale Blätter ---

  createPlayCode: (id: string, collectResults: boolean) =>
    request<PlayCode>(`/documents/${id}/play-code`, {
      method: 'POST',
      body: JSON.stringify({ collectResults }),
    }),

  playCodes: (id: string) => request<{ codes: PlayCode[] }>(`/documents/${id}/play-codes`),

  deletePlayCode: (code: string) =>
    request<{ ok: true }>(`/play-codes/${code}`, { method: 'DELETE' }),

  submissions: (id: string) =>
    request<{ submissions: SubmissionSummary[] }>(`/documents/${id}/submissions`),

  shares: (id: string) => request<{ shares: ShareSummary[] }>(`/documents/${id}/shares`),

  createShare: (id: string, days?: number) =>
    request<{ token: string; tokenHash: string; url: string; expiresAt: string | null }>(
      `/documents/${id}/shares`,
      { method: 'POST', body: JSON.stringify(days ? { days } : {}) },
    ),

  deleteShare: (id: string, tokenHash: string) =>
    request<{ ok: true }>(`/documents/${id}/shares/${tokenHash}`, { method: 'DELETE' }),

  revisions: (id: string) =>
    request<{ revisions: RevisionSummary[] }>(`/documents/${id}/revisions`),

  restoreRevision: (id: string, revision: number) =>
    request<{ id: string; revision: number; restoredFrom: number; document: WorksheetDocument }>(
      `/documents/${id}/revisions/${revision}/restore`,
      { method: 'POST' },
    ),
};
